import { message, Modal, Select, Spin } from 'antd';
import { useEffect, useRef, useState } from "react";
import { connect } from 'react-redux';
import { useParams } from "react-router-dom";
import AddSPC from '../../components/spc/add_spc';
import { appUrl } from '../../redux/services/base';
import SPCService from "../../redux/services/spc";

const key = "updatable";

const SPCGraph = (props) => {
	const { spc_id } = useParams();
	const [frameUrl, setFrameUrl] = useState(null);

	// Machine status state — tracks whether the machine is currently down
	const [isMachineDown, setIsMachineDown] = useState(false);

	// Machine Down Reason modal state
	const [reasonModalVisible, setReasonModalVisible] = useState(false);
	const [reasons, setReasons] = useState([]);
	const [selectedReasonId, setSelectedReasonId] = useState(null);
	const [reasonsLoading, setReasonsLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		setFrameUrl(`${appUrl}/spc/graph/react?page=1&spc_id=${spc_id}&token=`);

		// Load the real machine status from the backend on mount
		SPCService.fetchSPC(spc_id)
			.then(res => {
				// fetchSPC returns response.data directly (see service)
				setIsMachineDown(res?.is_machine_down === true);
			})
			.catch(() => {
				// If fetch fails, default stays false — non-critical
			});
	}, [spc_id]);

	const onSuccess = (isDataAdded=false) => {
		setFrameUrl(`${appUrl}/spc/graph/react?page=1&spc_id=${spc_id}&token=&ts=${new Date().valueOf()}`);
		if(isDataAdded) {
			setIsMachineDown(false);
		}
	};

	// ----------------------------------------------------------------
	// Machine Down / Up logic
	// ----------------------------------------------------------------

	const onMachineDown = (currentIsMachineDown) => {
		if (currentIsMachineDown) {
			// Machine is currently DOWN → mark it UP directly, no popup needed
			markMachineUp();
		} else {
			// Machine is currently UP → show reason selection popup before marking down
			openReasonModal();
		}
	};

	const openReasonModal = () => {
		setSelectedReasonId(null);
		setReasonModalVisible(true);
		setReasonsLoading(true);

		SPCService.getMachineDownReasons()
			.then(res => {
				setReasons(res.data?.results || []);
			})
			.catch(err => {
				message.error({ content: 'Failed to load reasons. Please try again.', key });
				setReasonModalVisible(false);
			})
			.finally(() => {
				setReasonsLoading(false);
			});
	};

	const handleReasonSubmit = () => {
		if (!selectedReasonId) {
			message.warning('Please select a reason before submitting.');
			return;
		}

		setSubmitting(true);
		SPCService.updateMachineStatus(spc_id, {
			is_machine_down: true,
			machine_down_reason_id: selectedReasonId,
		})
			.then(res => {
				message.success({ content: res.data.message, key });
				setIsMachineDown(true);
				setReasonModalVisible(false);
				onSuccess();
			})
			.catch(res => {
				message.error({ content: res?.response?.data?.message || 'Something went wrong.', key });
			})
			.finally(() => {
				setSubmitting(false);
			});
	};

	const markMachineUp = () => {
		if (!window.confirm('Are you sure you want to mark the machine as running (UP)?')) return;

		SPCService.updateMachineStatus(spc_id, { is_machine_down: false })
			.then(res => {
				message.success({ content: res.data.message, key });
				setIsMachineDown(false);
				onSuccess();
			})
			.catch(res => {
				message.error({ content: res?.response?.data?.message || 'Something went wrong.', key });
			});
	};

	// ----------------------------------------------------------------

	const onDelete = () => {
		if (window.confirm('Are you sure want to delete last data point?')) {
			SPCService.deleteDataPoint(spc_id).then(res => {
				message.success({ content: res.data.message, key });
				onSuccess();
			}).catch(res => {
				message.error({ content: res.response.data.message, key });
			});
		}
	};

	const onCalculateLimit = () => {
		if (window.confirm('Are you sure want to calculate UCL/LCL?')) {
			SPCService.calculateLimit(spc_id).then(res => {
				message.success({ content: res.data.message, key });
				onSuccess();
			}).catch(res => {
				message.error({ content: res.response.data.message, key });
			});
		}
	};

	return (
		<div style={{ width: '100%', height: 'calc(100% - 2px)' }}>
			<AddSPC
				spcId={spc_id}
				onSuccess={onSuccess}
				onCalculateLimit={onCalculateLimit}
				onRefresh={onSuccess}
				onDelete={onDelete}
				isMachineDown={isMachineDown}
				onMachineDown={() => onMachineDown(isMachineDown)}
			/>

			{
				frameUrl != null ? (
					<iframe src={frameUrl}
						style={{ width: '100%', height: 'calc(100% - 2px)', overflow: 'hidden', border: 'none' }} scrolling="yes">
					</iframe>
				) : null
			}

			{/* Machine Down Reason Modal */}
			<Modal
				title="Select Reason for Machine Down"
				visible={reasonModalVisible}
				onOk={handleReasonSubmit}
				onCancel={() => !submitting && setReasonModalVisible(false)}
				okText="Submit"
				cancelText="Cancel"
				confirmLoading={submitting}
				okButtonProps={{ disabled: !selectedReasonId || submitting }}
			>
				{reasonsLoading ? (
					<div style={{ textAlign: 'center', padding: '20px 0' }}>
						<Spin tip="Loading reasons..." />
					</div>
				) : (
					<>
						<p style={{ marginBottom: 12, color: '#555' }}>
							Please select the reason why the machine is being marked as <strong>down</strong>:
						</p>
						<Select
							placeholder="Select a reason"
							style={{ width: '100%' }}
							value={selectedReasonId}
							onChange={(val) => setSelectedReasonId(val)}
							options={reasons.map(r => ({
								label: r.reason,
								value: r.id,
							}))}
						/>
					</>
				)}
			</Modal>
		</div>
	);
};



function mapStateToProps(state) {
	const { user } = state.auth;

	return {
		user
	}
}

export default connect(mapStateToProps)(SPCGraph);